import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Send, Mic, Loader2, ChevronRight, Pause, X } from 'lucide-react';

interface Pending {
  name: string;
  size: number;
  mime: string;
  uri?: string;        // populated once upload finishes
  uploading?: boolean;
  error?: string;
}

interface Props {
  disabled: boolean;
  isWaitingUser: boolean;
  isActive: boolean;
  onSend: (text: string, attachments: Pending[]) => Promise<void>;
  onPause: () => Promise<void>;
  onCancel: () => Promise<void>;
  onSlash: (cmd: string) => void;
  uploadFile: (f: File) => Promise<{ name: string; size: number; mime: string; uri: string; kind: string }>;
  /** When this object changes, its `text` is loaded into the input. */
  insertSignal?: { text: string; nonce: number } | null;
}

const SLASH_COMMANDS = [
  { cmd: '/pause',  label: 'Pausar al taller', hint: 'Pausa el motor' },
  { cmd: '/resume', label: 'Reanudar', hint: 'Continúa donde quedó' },
  { cmd: '/clear',  label: 'Nueva conversación', hint: 'Archiva y empieza limpio' },
  { cmd: '/repo',   label: 'Estado del repo', hint: 'Muestra info del código' },
  { cmd: '/export', label: 'Exportar conversación', hint: 'Descarga como Markdown' },
];

export const ComposerBar: React.FC<Props> = ({
  disabled, isWaitingUser, isActive, onSend, onPause, onCancel, onSlash,
  uploadFile, insertSignal,
}) => {
  const [input, setInput] = useState('');
  const [showSlash, setShowSlash] = useState(false);
  const [pendings, setPendings] = useState<Pending[]>([]);
  const [sending, setSending] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const textRef = useRef<HTMLTextAreaElement | null>(null);

  // Load prompts pushed from the WorkspaceContextChips (quick-actions).
  // Focuses the textarea + selects everything so the user can tweak
  // before sending.
  useEffect(() => {
    if (!insertSignal || !insertSignal.text) return;
    setInput(insertSignal.text);
    // Defer to next tick so the DOM has the new value.
    requestAnimationFrame(() => {
      if (textRef.current) {
        textRef.current.focus();
        textRef.current.select();
        textRef.current.style.height = 'auto';
        textRef.current.style.height = `${Math.min(textRef.current.scrollHeight, 120)}px`;
      }
    });
  }, [insertSignal]);

  const onChange = (v: string) => {
    setInput(v);
    setShowSlash(v.startsWith('/') && v.length <= 10);
    // Auto-grow
    if (textRef.current) {
      textRef.current.style.height = 'auto';
      textRef.current.style.height = `${Math.min(textRef.current.scrollHeight, 120)}px`;
    }
  };

  const send = async () => {
    const text = input.trim();
    if (text.startsWith('/')) { onSlash(text.split(/\s+/)[0]); setInput(''); setShowSlash(false); return; }
    if (!text && pendings.length === 0) return;
    setSending(true);
    try {
      await onSend(text || '(adjuntos)', pendings);
      setInput(''); setPendings([]);
      if (textRef.current) textRef.current.style.height = 'auto';
    } finally { setSending(false); }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
    if (e.key === 'Escape') setShowSlash(false);
  };

  const handleFiles = async (files: FileList | null) => {
    if (!files) return;
    const arr = Array.from(files);
    for (const f of arr) {
      const draft: Pending = { name: f.name, size: f.size, mime: f.type, uploading: true };
      setPendings((prev) => [...prev, draft]);
      try {
        const meta = await uploadFile(f);
        setPendings((prev) => prev.map((p) =>
          p === draft ? { ...p, uri: meta.uri, uploading: false } : p
        ));
      } catch (e: any) {
        setPendings((prev) => prev.map((p) =>
          p === draft ? { ...p, uploading: false, error: e?.message || 'falló' } : p
        ));
      }
    }
  };

  return (
    <div className="px-3 sm:px-5 pb-2 pt-1 relative chat-safe-bottom">
      <AnimatePresence>
        {showSlash && (
          <motion.div
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            className="absolute bottom-[72px] left-3 right-3 sm:left-5 sm:right-5 bg-[#0E0F14] border border-white/[0.08] rounded-2xl overflow-hidden shadow-2xl z-10"
            data-testid="chat-slash-palette"
          >
            {SLASH_COMMANDS.filter((c) => c.cmd.startsWith(input))
              .map((c) => (
                <button
                  type="button" key={c.cmd}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => { onSlash(c.cmd); setInput(''); setShowSlash(false); }}
                  className="w-full px-4 py-2.5 flex items-center justify-between gap-3 hover:bg-white/[0.04] text-left transition-colors"
                >
                  <div>
                    <div className="text-sm text-zinc-100 font-mono">{c.cmd}</div>
                    <div className="text-[11px] text-zinc-500">{c.hint}</div>
                  </div>
                  <ChevronRight className="w-3.5 h-3.5 text-zinc-600" />
                </button>
              ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Pending attachments preview */}
      {pendings.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {pendings.map((p, i) => (
            <div key={i} className="text-[11px] inline-flex items-center gap-1 bg-white/[0.04] border border-white/10 rounded-full px-2 py-1 text-zinc-300">
              {p.uploading && <Loader2 className="w-3 h-3 animate-spin" />}
              <span className="truncate max-w-[140px]">{p.name}</span>
              {p.error && <span className="text-rose-300">·{p.error}</span>}
              <button type="button" onClick={() => setPendings((prev) => prev.filter((x) => x !== p))} className="text-zinc-500 hover:text-rose-300">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* If active — show pause/cancel above composer */}
      {isActive && (
        <div className="mb-2 flex gap-1.5">
          <button
            type="button"
            onClick={onPause}
            className="inline-flex items-center gap-1 bg-white/[0.06] hover:bg-white/[0.12] border border-white/10 text-zinc-100 text-xs px-2.5 py-1.5 rounded-full transition-colors"
            data-testid="chat-pause-btn"
          >
            <Pause className="w-3 h-3" /> Pausar
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center gap-1 bg-rose-500/10 hover:bg-rose-500/20 border border-rose-400/20 text-rose-200 text-xs px-2.5 py-1.5 rounded-full transition-colors"
            data-testid="chat-cancel-btn"
          >
            <X className="w-3 h-3" /> Cancelar
          </button>
        </div>
      )}

      <div className="flex items-end gap-2 bg-[#0E0F14] border border-white/[0.08] rounded-3xl pl-2 pr-2 py-1.5 focus-within:border-violet-400/40 transition-colors">
        <input
          ref={fileRef}
          type="file"
          multiple
          accept="image/*,.pdf,.txt,.md,.csv,.json"
          onChange={(e) => handleFiles(e.target.files)}
          className="hidden"
          data-testid="chat-file-input"
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={disabled}
          className="w-9 h-9 rounded-full bg-white/[0.04] hover:bg-white/[0.10] grid place-items-center text-zinc-400 hover:text-white transition-colors shrink-0 self-end"
          data-testid="chat-attach-btn"
          title="Adjuntar archivo o imagen"
        >
          <Plus className="w-4 h-4" />
        </button>
        <textarea
          ref={textRef}
          value={input}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={
            isWaitingUser ? 'Responde para continuar…' : 'Escribe tu mensaje…'
          }
          rows={1}
          data-testid="chat-input"
          disabled={sending}
          className="flex-1 resize-none bg-transparent border-0 outline-none text-base sm:text-sm text-zinc-100 placeholder-zinc-500 py-2 px-1 max-h-32 leading-snug"
          style={{ minHeight: '24px' }}
          autoCapitalize="sentences"
          autoComplete="off"
          autoCorrect="on"
          enterKeyHint="send"
        />
        <button
          type="button"
          disabled
          className="w-9 h-9 rounded-full bg-white/[0.04] grid place-items-center text-zinc-600 shrink-0 self-end hidden sm:grid"
          title="Voz (próximamente)"
        >
          <Mic className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={send}
          disabled={(!input.trim() && pendings.length === 0) || sending}
          data-testid="chat-send-btn"
          className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-500 to-violet-700 hover:from-violet-400 hover:to-violet-600 grid place-items-center text-white shrink-0 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-lg shadow-violet-900/30 self-end"
        >
          {sending
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : <Send className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
};
